import React from "react"
import { generatePath, useNavigate } from "react-router-dom"
import { observer } from "mobx-react"
import { Table, Tag, Typography } from "antd"
import { ColumnsType } from "antd/es/table"

import { GlobalCurrenciesCollection } from "@src/app/CurrenciesCollection"
import { paths } from "@src/app/paths"
import { ConvertsCollection } from "@src/pages/transactionsSwap/classes/ConvertsCollection"
import {
  ConvertResponse,
  ConvertResponseStatusEnum,
} from "@src/shared/api/generated"
import { WithCollection } from "@src/shared/classes/Collection/types"
import { DateTimeTableCell } from "@src/shared/componemts/DateTimeTableCell"
import { TableExpandable } from "@src/shared/componemts/TableExpandable"
import { WithCopy } from "@src/shared/componemts/WithCopy"
import { dateTimeFormat } from "@src/shared/date"
import { paddingLimit } from "@src/shared/strings"

function ListExpandable({
  convert,
  isLoading,
}: {
  convert: ConvertResponse
  isLoading: boolean
}) {
  const {
    amount_from,
    amount_to,
    created_at,
    id,
    network_from,
    network_to,
    rate,
    status,
    symbol_from,
    symbol_to,
  } = convert

  const cMap = {
    [ConvertResponseStatusEnum.Failed]: "error",
    [ConvertResponseStatusEnum.Pending]: "geekblue",
    [ConvertResponseStatusEnum.Success]: "green",
  }

  const currencyFrom = GlobalCurrenciesCollection().getBySymbolNetwork({
    network: network_from as string,
    symbol: symbol_from as string,
  })

  const currencyTo = GlobalCurrenciesCollection().getBySymbolNetwork({
    network: network_to as string,
    symbol: symbol_to as string,
  })

  return (
    <TableExpandable
      isLoading={isLoading}
      list={[
        ["ID", <WithCopy content={id!} />],

        ["Network From", currencyFrom?.network ?? network_from],
        ["Symbol From", currencyFrom?.symbol_alias ?? symbol_from],
        [
          "Amount From",
          <Typography.Text style={{ fontWeight: 600 }}>
            {amount_from}
          </Typography.Text>,
        ],
        ["Network To", currencyTo?.network ?? network_to],
        ["Symbol To", currencyTo?.symbol_alias ?? symbol_to],
        [
          "Amount To",
          <Typography.Text style={{ fontWeight: 600 }}>
            {amount_to}
          </Typography.Text>,
        ],
        // ["Rate", rate],
        [
          "Status",
          <Tag color={cMap[status ?? ConvertResponseStatusEnum.Pending]}>
            {status}
          </Tag>,
        ],
        ["Created at", dateTimeFormat(created_at!)],
      ]}
    />
  )
}

export const SwapTable = observer(
  ({
    collection,
    replace,
  }: WithCollection<ConvertsCollection> & { replace?: boolean }) => {
    const swapColumns: ColumnsType<ConvertResponse> = [
      {
        dataIndex: "id",
        render: (_, { id }) => (
          <WithCopy
            content={id as string}
            style={{ justifyContent: "space-between", width: "100%" }}
            view={<span>{paddingLimit(id as string, 0, 4)}</span>}
          />
        ),
        title: "ID",
        width: 100,
      },
      {
        align: "center",
        dataIndex: "network_from",
        title: "Network From",
      },
      {
        align: "center",
        dataIndex: "symbol_from",
        render: (_, { network_from, symbol_from }) => {
          const c = GlobalCurrenciesCollection().getBySymbolNetwork({
            network: network_from as string,
            symbol: symbol_from as string,
          })

          return (
            <Typography.Text>{c?.symbol_alias ?? symbol_from}</Typography.Text>
          )
        },
        title: "Symbol From",
      },
      {
        align: "center",
        dataIndex: "amount_from",
        render: (_, { amount_from }) => (
          <Typography.Text style={{ fontWeight: 600 }}>
            {amount_from}
          </Typography.Text>
        ),
        title: "Amount From",
      },
      {
        align: "center",
        dataIndex: "network_to",
        title: "Network To",
      },
      {
        dataIndex: "symbol_to",
        render: (_, { network_to, symbol_to }) => {
          const c = GlobalCurrenciesCollection().getBySymbolNetwork({
            network: network_to as string,
            symbol: symbol_to as string,
          })

          return (
            <Typography.Text>{c?.symbol_alias ?? symbol_to}</Typography.Text>
          )
        },
        title: "Symbol To",
      },
      {
        dataIndex: "amount_to",
        render: (_, { amount_to }) => (
          <Typography.Text style={{ fontWeight: 600 }}>
            {amount_to}
          </Typography.Text>
        ),
        title: "Amount To",
      },
      {
        dataIndex: "status",
        render: (_, { status }) => {
          const cMap = {
            [ConvertResponseStatusEnum.Failed]: "error",
            [ConvertResponseStatusEnum.Pending]: "geekblue",
            [ConvertResponseStatusEnum.Success]: "green",
          }

          return (
            <Tag color={cMap[status ?? ConvertResponseStatusEnum.Pending]}>
              {status}
            </Tag>
          )
        },
        title: "Status",
      },
      {
        dataIndex: "created_at",
        render: (_, { created_at }) => <DateTimeTableCell value={created_at} />,
        title: "Created at",
      },
    ]

    const nav = useNavigate()

    const onRowClick = (row: ConvertResponse) => {
      nav(generatePath(paths.swapDetails, { id: row.id! }))
    }

    return (
      <Table
        columns={swapColumns}
        dataSource={replace ? collection.items.slice(0, 10) : collection.items}
        expandable={{
          expandedRowRender: (record) => (
            <ListExpandable convert={record} isLoading={collection.isLoading} />
          ),
        }}
        loading={collection.isLoading}
        pagination={replace ? false : collection.pagination}
        rowKey="id"
        scroll={{ x: true }}
      />
    )
  },
)
